import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Skeleton } from 'primereact/skeleton';
import { Toast } from 'primereact/toast';
import { FileClock } from 'lucide-react';

import { checkPermission, DATE_FORMATS } from '../../../utils/Helpers';
import permissions from '../../../config/permissions';
import CheckboxList from '../../shared/CheckboxList/checkboxList';
import TableColumnsLoader from '../../shared/Loader/tableColumnsLoader';
import { formatPFDateTime } from '../../../utils/date.helper';
import PageHeaderBreadcrumb from '../../shared/PageHeader/PageHeaderBreadcrumb';

import CreateDashboardDialog from './CreateDashboardDialog';
import {
  ColumnMetaType,
  DashboardListItems,
  DashboardResponse,
  DataArrayType,
  ItemType,
  DashboardListProps,
  RowDataType,
} from './interface/DashboardList.interface';
import { SOURCE } from './constants/constants';
import {
  deleteDashboard,
  fetchDashboardList,
} from './services/Dashboards.service';

const schedulerBreadcrumb = [
  {
    label: 'Reports & Logs',
    icon: FileClock,
  },
  {
    label: 'Reports',
  },
  {
    url: '/admin/dashboards',
    label: 'Dashboards',
  },
];

const DashboardList: React.FC<DashboardListProps> = ({
  source,
  dashboardData,
  linkDashboard,
  detachDashboard,
  selectedItems,
  setSelectedItems,
  updateDefaultDashboardId,
  defaultDashboardId,
}) => {
  const detachDashboardPermission = checkPermission(
    permissions?.home?.detachDashboard
  );
  const attachDashboardPermission = checkPermission(
    permissions?.home?.attachDashboard
  );

  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState({
    limit: 100,
    offset: 0,
    sortKey: '',
    sortDirection: '',
  });
  const [dashboardListData, setDashboardListData] = useState<DashboardResponse>(
    { count: 0, items: [] }
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const history = useHistory();
  const [confirmDialog, setConfirmDialog] = useState({
    header: '',
    title: '',
    subtitle: '',
    isOpen: false,
  });
  const [rowIndex, setRowIndex] = useState<number>();
  const toast = useRef<Toast>(null);
  const isCloneRef = useRef(false);
  const isEditRef = useRef(false);
  const searchTerm = '';
  const sortBy = '';
  const sortOrder = 'null';
  const [listSearchTerm, setListSearchTerm] = useState('');
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCheckboxChange = (item: ItemType) => {
    setSelectedItems(prevItems => {
      const index = prevItems.findIndex(i => i.name === item.name);
      if (index >= 0) {
        return [...prevItems.slice(0, index), ...prevItems.slice(index + 1)];
      } else {
        return [...prevItems, item];
      }
    });
  };

  const columns = [
    {
      key: 'name',
      label: 'Name',
      showLabel: true,
      isTitle: true,
      isSubTitle: false,
      bold: false,
      italic: false,
    },
    {
      key: 'description',
      label: 'Description',
      showLabel: false,
      isTitle: false,
      isSubTitle: true,
      bold: false,
      italic: false,
    },
    {
      key: 'created_by',
      label: 'Created By',
      showLabel: false,
      isTitle: false,
      isSubTitle: false,
      bold: true,
      italic: false,
    },
    {
      key: 'created_at',
      label: 'Date Created',
      showLabel: false,
      isTitle: false,
      isSubTitle: false,
      bold: false,
      italic: true,
    },
  ];

  useEffect(() => {
    toast?.current?.clear();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    const { dashboardList } = await fetchDashboardList(searchQuery, source);

    if (dashboardList?.items) {
      setDashboardListData(dashboardList);
      const defaultDashboard = dashboardList?.items?.find(
        (record: DashboardListItems) => record?.is_default_dashboard
      );

      if (defaultDashboard) {
        updateDefaultDashboardId(defaultDashboard.dashboard_id);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [searchQuery]);

  const handleDelete = (dataIndex: number) => {
    setRowIndex(dataIndex);
    setConfirmDialog({
      ...confirmDialog,
      isOpen: true,
      title:
        'Are you sure you want to delete the Dashboard? You will not be able to recover this again?',
      header: 'Delete Dashboard',
    });
  };
  const onConfirmDialog = async () => {
    let dashboardId;
    setLoading(true);
    setConfirmDialog(prevState => ({
      ...prevState,
      isOpen: false,
    }));
    if (typeof rowIndex === 'number') {
      dashboardId = dashboardListData?.items[rowIndex]?.dashboard_id;
    }
    const result = await deleteDashboard(dashboardId);
    if (!result?.error) {
      const { dashboardList } = await fetchDashboardList(searchQuery);
      setDashboardListData(dashboardList);
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: result?.message,
        life: 3000,
      });
    } else {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: result?.message,
        life: 3000,
      });
    }
    setLoading(false);
  };

  const handleClone = (dataIndex: number) => {
    setRowIndex(dataIndex);
    setDialogOpen(true);
    isCloneRef.current = true;
  };
  const handleEdit = (dataIndex: number) => {
    setRowIndex(dataIndex);
    setDialogOpen(true);
    isEditRef.current = true;
  };

  const dataTableColumns: ColumnMetaType[] = [
    {
      field: '',
      header: '',
      style: { minWidth: '8rem', maxWidth: '10rem' },
      body: (rowData: RowDataType, { rowIndex }: { rowIndex: number }) => {
        if (loading) {
          return <Skeleton className="w-full" />;
        }
        rowIndex = rowIndex - (searchQuery.offset > 0 ? searchQuery.offset : 0);
        return (
          <div className="flex gap-2 lg:gap-3 lg:h-2rem align-items-center flex-wrap">
            {checkPermission(permissions?.dashboardManagement?.menu) && (
              <span>
                <i
                  className="pi pi-eye cursor-pointer text-primary"
                  onClick={() =>
                    history.push(
                      `/admin/dashboard/view/${dashboardListData?.items[rowIndex].dashboard_id}`
                    )
                  }
                ></i>
              </span>
            )}
            {checkPermission(
              permissions?.dashboardManagement?.editDashboard
            ) && (
              <span>
                <i
                  className="pi pi-pencil cursor-pointer text-primary "
                  onClick={() =>
                    history.push(
                      `/admin/dashboard/edit/${dashboardListData?.items[rowIndex]?.dashboard_id}?list=1`
                    )
                  }
                ></i>
              </span>
            )}
            {checkPermission(
              permissions?.dashboardManagement?.copyDashboard
            ) && (
              <span>
                <i
                  className="pi pi-file-edit cursor-pointer text-gray-500"
                  onClick={() => handleEdit(rowIndex)}
                ></i>
              </span>
            )}
            {checkPermission(
              permissions?.dashboardManagement?.copyDashboard
            ) && (
              <span>
                <i
                  className="pi pi-copy cursor-pointer text-green-500"
                  onClick={() => handleClone(rowIndex)}
                ></i>
              </span>
            )}
            {checkPermission(
              permissions?.dashboardManagement?.deleteDashboard
            ) && (
              <span>
                <i
                  className="pi pi-trash cursor-pointer text-red-500"
                  onClick={() => handleDelete(rowIndex)}
                ></i>
              </span>
            )}
          </div>
        );
      },
    },
    {
      field: 'name',
      header: 'Name',
      sortable: true,
      style: { minWidth: '9rem', maxWidth: '10rem' },
    },
    {
      field: 'description',
      header: 'Description',
      sortable: true,
      style: { minWidth: '9rem', maxWidth: '10rem' },
    },
    {
      field: 'created_by',
      header: 'Created By',
      sortable: true,
      style: { minWidth: '9rem', maxWidth: '10rem' },
    },
    {
      field: 'created_at',
      header: 'Date Created',
      sortable: true,
      style: { minWidth: '9rem', maxWidth: '10rem' },
    },
  ];

  const tableLoader = useMemo(() => {
    return TableColumnsLoader(dataTableColumns, {
      rows: searchQuery?.limit,
      isValue: false,
    });
  }, [dataTableColumns]);

  if (source === SOURCE.HOME) {
    dataTableColumns.unshift({
      field: 'icons',
      header: 'Add/Remove',
      style: { width: '8%' },
    });
  }

  const dataArray: DataArrayType[] =
    dashboardListData?.items?.map(val => {
      return {
        icons: dashboardData?.filter(
          value => value?.dashboard_id === val?.dashboard_id
        )?.length ? (
          <div className="flex justify-content-center">
            <Button
              outlined
              color="primary"
              label="Remove"
              onClick={() => {
                setLoading(true);
                detachDashboard(val?.dashboard_id);
              }}
              disabled={!detachDashboardPermission}
              size="small"
              icon="pi pi-trash"
              className="height-40"
            />
          </div>
        ) : (
          <div className="flex justify-content-center">
            <Button
              label="Add"
              disabled={!attachDashboardPermission}
              onClick={() => {
                setLoading(true);
                linkDashboard(val?.dashboard_id);
              }}
              size="small"
              icon="pi pi-plus"
              className="height-40"
            />
          </div>
        ),
        created_at: val.created_at
          ? formatPFDateTime(val.created_at, DATE_FORMATS.FULL_DATE_TIME)
          : null,
        name: val.name,
        description: val.description,
        created_by: val.created_by,
        id: val.dashboard_id,
      };
    }) ?? [];

  const handleSearchQuery = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchString = e?.target?.value?.trim();
    setListSearchTerm(e?.target?.value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setLoading(true);
      setSearchQuery(prevState => ({
        ...prevState,
        offset: 0,
        search_query: searchString,
      }));
    }, 500);
  };

  const viewPermission = checkPermission(
    permissions?.dashboardManagement?.menu
  );

  const handleClearSearch = () => {
    setListSearchTerm('');
    setLoading(true);
    setSearchQuery(prevState => ({
      ...prevState,
      offset: 0,
      search_query: '',
    }));
  };

  const renderHeader = () => {
    return (
      <div className="flex justify-content-end align-items-center w-full">
        <span className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText
            value={listSearchTerm}
            onChange={handleSearchQuery}
            placeholder="Keyword Search"
            className="p-inputtext-sm"
            pt={{
              root: {
                className: 'w-full md:w-20rem',
              },
            }}
          />
          {listSearchTerm && (
            <i
              className="pi pi-times cursor-pointer mr-2 absolute-right"
              onClick={handleClearSearch}
              title="Clear"
            />
          )}
        </span>
      </div>
    );
  };
  const addDashboard = () => {
    setDialogOpen(true);
  };
  const buttonArray = [
    {
      label: 'Add New Dashboard',
      icon: 'pi pi-plus',
      outlined: false,
      action: addDashboard,
      visible: checkPermission(
        permissions?.dashboardManagement?.createDashboard
      ),
    },
  ];

  return (
    <div className="w-full">
      {!dialogOpen && <Toast ref={toast} />}
      {!source && (
        <div className="flex justify-content-between align-items-center w-full">
          <div className="w-full">
            <PageHeaderBreadcrumb
              headerValue="Dashboards"
              breadcrumbs={schedulerBreadcrumb}
              buttonArray={buttonArray}
              identifier="dashboard"
            />
          </div>

          {checkPermission(
            permissions?.dashboardManagement?.createDashboard
          ) && (
            <div>
              <CreateDashboardDialog
                dialogOpen={dialogOpen}
                setDialogOpen={setDialogOpen}
                cloneValues={
                  typeof rowIndex === 'number' &&
                  dashboardListData?.items?.length > 0 &&
                  dashboardListData?.items?.[rowIndex]
                    ? dashboardListData.items[rowIndex]
                    : undefined
                }
                isCloneRef={isCloneRef}
                isEditRef={isEditRef}
                fetchData={fetchData}
              />
            </div>
          )}
        </div>
      )}
      <div className="w-full mt-3">
        {source !== SOURCE.HOME && (
          <DataTable
            value={loading ? tableLoader : dataArray}
            paginator={!loading}
            rows={searchQuery.limit}
            rowsPerPageOptions={[10, 20, 50, 100]}
            totalRecords={dashboardListData?.count || 0}
            lazy
            first={searchQuery.offset}
            sortField={searchQuery?.sortKey}
            sortOrder={searchQuery?.sortDirection === 'asc' ? 1 : -1}
            onPage={e => {
              setSearchQuery(prev => ({
                ...prev,
                limit: e.rows,
                offset: e.first,
              }));
            }}
            onSort={e => {
              setSearchQuery(prevState => ({
                ...prevState,
                sortKey: e.sortField,
                sortDirection: e.sortOrder === 1 ? 'asc' : 'desc',
              }));
            }}
            {...(viewPermission
              ? {
                  onRowDoubleClick: e => {
                    const rowIndex = e?.index;
                    if (!source) {
                      history.push(
                        `/admin/dashboard/view/${
                          dashboardListData?.items?.length &&
                          dashboardListData?.items[rowIndex]?.dashboard_id
                        }?list=1`
                      );
                    }
                  },
                }
              : {})}
            emptyMessage={
              !loading && !dataArray.length ? 'No records found' : null
            }
            pt={{
              table: { className: 'w-full' },
              bodyRow: { className: 'cursor-pointer h-4rem' },
              header: {
                className: 'py-2 bg-blue-50  border-top-none',
              },
            }}
            header={renderHeader}
            className="w-full pf-data-table p-datatable-striped"
          >
            {dataTableColumns?.map((col, index) => (
              <Column
                key={index}
                field={col?.field}
                header={col?.header}
                sortable={col?.sortable}
                style={col?.style}
                body={col?.body}
                pt={{
                  headerContent: { className: 'cursor-auto' },
                  headerCell: { className: 'cursor-auto' },
                  sort: { className: 'cursor-pointer' },
                }}
              />
            ))}
          </DataTable>
        )}
        {source === SOURCE.HOME && (
          <CheckboxList
            columns={columns}
            data={dataArray as never[]}
            selectedItems={selectedItems}
            onCheckboxChange={handleCheckboxChange}
            searchTerm={searchTerm}
            sortBy={sortBy}
            sortOrder={sortOrder}
            unselectedBackground="white"
            loading={loading} // You can change this to any color you want
            updateDefaultDashboardId={updateDefaultDashboardId}
            defaultDashboardId={defaultDashboardId}
          />
        )}
      </div>
      <ConfirmDialog
        draggable={false}
        group="declarative"
        visible={confirmDialog.isOpen}
        message={confirmDialog.title}
        header={confirmDialog.header}
        icon="pi pi-exclamation-triangle"
        accept={onConfirmDialog}
        reject={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        className="w-11 lg:w-5 xl:w-4 pf-confirm-dialog"
        pt={{
          headerIcons: {
            className: 'hidden',
          },
          acceptButton: {
            className: 'height-40',
          },
          rejectButton: {
            className: 'height-40 p-button-outlined',
          },
        }}
        acceptLabel="Delete"
        rejectLabel="Cancel"
      />
    </div>
  );
};

export default DashboardList;
